import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { useSnapshot } from "valtio"
import { useQuery } from "@tanstack/react-query"
import useApi from "../../hooks/useApi"
import useDebounce from "../../hooks/useDebounce"
import useTranslation from "../../hooks/useTranslation"
import useProfile from "../../hooks/useProfile"
import { FormGroup } from "../form/FormComponents"
import FlagsPng from "../country/FlagsPng"
import { CountryFlag } from "../country/CountryMenuItem"
import { registerFor, searchMode, pickChanges, registerLabel, registerText, providerText } from "./companyLookupLogic"

/*
 * A text field that also looks the company up in a company register
 * (task 39.01). The typed text is the field's value like any text input;
 * picking a suggestion writes every field in `valueMap` at once.
 *
 * `valueMap` maps a form field to a path in the CompanyInfo summary:
 *   { name: 'name', taxId: 'companyId', address_street: 'address.street' }
 *
 * The flag on the left is the register searched, and a click on it chooses
 * another: the countries with a register (GET company-info/registers) and
 * "EU – VAT number only" (VIES, which finds a company by its full VAT number
 * and nothing else). Until one is chosen it is the form's `country`, else the
 * organisation's — a contact created on the spot from a picker starts without
 * one.
 *
 * Picking a company always sets the form's country (id and display name) to
 * the company's: the register says where it is, so the saved record says
 * which register its tax id belongs to. A register that has no provider
 * answers UNSUPPORTED_COUNTRY and the field is then an ordinary text input,
 * with the flag still there to choose one. Only what the user types is looked
 * up — opening an existing record does not query anything.
 *
 * The decisions are in companyLookupLogic.js.
 */

const DEBOUNCE_MS = 400

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`

// The flag sits inside the input's left edge, as on the country field.
const FlagButton = styled.button`
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40px;
    display: flex;
    align-items: center;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
`

const Input = styled(FormGroup.Control)`
    ${({ $withFlag }) => $withFlag && 'padding-left: 45px;'}
`

const Suggestions = styled.ul`
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 320px;
    overflow-y: auto;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: ${({ theme }) => theme.dropdown.background};
    border: 1px solid ${({ theme }) => theme.dropdown.border};
    border-radius: 6px;
`

const Suggestion = styled.li`
    padding: 6px 12px;
    cursor: pointer;
    background: ${({ theme, $active }) => ($active ? theme.dropdown.selectedBackground : 'transparent')};
    color: ${({ theme, $active }) => ($active ? theme.dropdown.selectedFont : theme.font.color)};

    &:hover {
        background: ${({ theme }) => theme.dropdown.selectedBackground};
        color: ${({ theme }) => theme.dropdown.selectedFont};
    }
`

const RegisterRow = styled(Suggestion)`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-left: 4px;
`

const Primary = styled.div`
    font-weight: 600;
`

const Secondary = styled.div`
    font-size: 0.85em;
    color: ${({ theme }) => theme.font.label};
`

const Note = styled.li`
    padding: 6px 12px;
    font-size: 0.85em;
    color: ${({ theme }) => theme.font.label};
`

const CompanyLookup = ({ name, inputId, value, setValue, placeholder, country: formCountry, valueMap = {}, required, inputRef, testId }) => {
    const { t } = useTranslation()
    const { getMe } = useApi()
    const { pState } = useProfile()
    const profile = useSnapshot(pState)

    const [chosen, setChosen] = useState(null)        // a register the user picked by its flag
    const register = registerFor(chosen, formCountry, profile.currentOrgCountry)

    const [typed, setTyped] = useState(false)
    const [open, setOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [results, setResults] = useState(null)     // null = nothing looked up yet
    const [loading, setLoading] = useState(false)
    const [active, setActive] = useState(-1)
    const [unsupported, setUnsupported] = useState({})
    const seq = useRef(0)
    const wrapperRef = useRef(null)

    const { data: registerData } = useQuery({
        queryKey: ['companyInfoRegisters'],
        queryFn: () => getMe('company-info/registers'),
        staleTime: Infinity
    })
    // getMe hands back the error response instead of throwing.
    const registers = Array.isArray(registerData) ? registerData : []
    const info = registers.find(r => r.id === register)

    const query = (value || '').trim()
    const debounced = useDebounce(query, DEBOUNCE_MS)
    const mode = typed && register && !unsupported[register] ? searchMode(info, debounced) : 'none'
    const searchable = mode === 'search'

    useEffect(() => {
        if (!searchable) {
            setResults(null)
            return
        }
        const mine = ++seq.current
        setLoading(true)
        getMe(`company-info/search?country=${encodeURIComponent(register)}&q=${encodeURIComponent(debounced)}`)
            .then((res) => {
                if (mine !== seq.current) return
                if (Array.isArray(res)) {
                    setResults(res)
                    setActive(-1)
                } else {
                    if (res?.data?.error === 'UNSUPPORTED_COUNTRY') setUnsupported(u => ({ ...u, [register]: true }))
                    setResults(null)
                }
            })
            .finally(() => { if (mine === seq.current) setLoading(false) })
        // getMe is a fresh function each render; the query inputs are what matter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced, register, searchable])

    // The register menu closes on a click anywhere else.
    useEffect(() => {
        if (!menuOpen) return
        const onDown = (e) => { if (!wrapperRef.current?.contains(e.target)) setMenuOpen(false) }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [menuOpen])

    const pick = (company) => {
        setValue(pickChanges(company, valueMap, name))
        setTyped(false)
        setOpen(false)
        setResults(null)
    }

    const chooseRegister = (id) => {
        setChosen(id)
        setMenuOpen(false)
        // What is already typed is looked up again, in the new register.
        if (query) {
            setTyped(true)
            setOpen(true)
        }
        inputRef?.current?.focus?.()
    }

    const onChange = (e) => {
        setTyped(true)
        setOpen(true)
        setValue(e)
    }

    const onKeyDown = (e) => {
        if (!open || !results?.length) return
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive(i => Math.min(i + 1, results.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive(i => Math.max(i - 1, 0))
        } else if (e.key === 'Enter' && active >= 0) {
            e.preventDefault()
            pick(results[active])
        } else if (e.key === 'Escape') {
            setOpen(false)
        }
    }

    const describe = (c) => {
        const place = [c.address?.street, [c.address?.postalCode, c.address?.city].filter(Boolean).join(' ')]
            .filter(Boolean).join(', ')
        return [`${registerText(t, 'companyId', c.registry ?? c.country)} ${c.companyId}`, place, c.status === 'ACTIVE' ? null : t('all.companyLookup.inactive')]
            .filter(Boolean).join(' · ')
    }

    const flag = register && FlagsPng[register]
    const showList = !menuOpen && open && (mode === 'vatOnly' || (searchable && (loading || results)))

    return (
        <Wrapper ref={wrapperRef}>
            {flag && (
                <FlagButton type='button' onClick={() => registers.length && setMenuOpen(o => !o)}
                    title={t('all.companyLookup.chooseRegister')} aria-label={t('all.companyLookup.chooseRegister')}
                    aria-haspopup='listbox' aria-expanded={menuOpen}
                    data-testid={`${testId}.register`}>
                    <CountryFlag src={flag} alt={register} />
                </FlagButton>
            )}
            <Input type='text' id={inputId} name={name} value={value || ''}
                $withFlag={!!flag}
                onChange={onChange} onKeyDown={onKeyDown}
                onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
                placeholder={providerText(t, register, 'placeholder') ?? placeholder} autoComplete='off'
                {...(required && { required: true })}
                {...(inputRef && { ref: inputRef })}
                role='combobox' aria-expanded={Boolean(showList)} aria-autocomplete='list'
                data-testid={testId} />
            {menuOpen && (
                <Suggestions role='listbox' data-testid={`${testId}.registers`}>
                    {registers.map(r => (
                        <RegisterRow key={r.id} role='option' aria-selected={r.id === register} $active={r.id === register}
                            onMouseDown={(e) => { e.preventDefault(); chooseRegister(r.id) }}
                            data-testid={`${testId}.register.${r.id}`}>
                            <CountryFlag src={FlagsPng[r.id]} alt='' />
                            {registerLabel(r, t)}
                        </RegisterRow>
                    ))}
                </Suggestions>
            )}
            {showList && (
                <Suggestions role='listbox' data-testid={`${testId}.suggestions`}>
                    {mode === 'vatOnly' && <Note>{registerText(t, 'hint', register)}</Note>}
                    {results?.map((c, i) => (
                        <Suggestion key={`${c.registry ?? c.country}-${c.companyId}`} role='option' aria-selected={i === active} $active={i === active}
                            onMouseDown={(e) => { e.preventDefault(); pick(c) }}>
                            <Primary>{c.name || c.companyId}</Primary>
                            <Secondary>{describe(c)}</Secondary>
                        </Suggestion>
                    ))}
                    {searchable && loading && !results?.length && <Note>{registerText(t, 'searching', register)}</Note>}
                    {searchable && !loading && results?.length === 0 && <Note>{registerText(t, 'noMatch', register)}</Note>}
                </Suggestions>
            )}
        </Wrapper>
    )
}

export default CompanyLookup
